// The Customers page's reads and its contact / ship-to address saves: ONE copy, used by the desktop
// Customers page (pages/CustomerEntryPage.jsx) and the phone (mobile/screens/CustomersScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page;
// pages/CustomerEntryPage.actions.test.jsx pins them. The customer's own save, deleting a ship-to and
// every tax-certificate action stay on the desktop.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';
import { apiJson, CUSTOMER_STATUS } from '../../utils/taxCertificates';

const API_BASE = config.API_BASE_URL;

// --- Reads ------------------------------------------------------------------------------------------

/** GET /customers, by name. Throws "Failed to load customers list". */
export async function loadCustomers() {
  const res = await fetch(`${API_BASE}/customers`);
  if (!res.ok) throw new Error('Failed to load customers list');
  const data = await res.json();
  return data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** The list's search: the name or the main contact contains it (any case; as typed, not trimmed). */
export function filterCustomers(list, searchTerm) {
  const s = String(searchTerm || '').toLowerCase();
  return (list || []).filter((cust) => {
    if (!s) return true;
    return (cust.name || '').toLowerCase().includes(s) || (cust.contact_name || '').toLowerCase().includes(s);
  });
}

/** GET /customers/{id}. Throws "Failed to load customer details.". */
export async function loadCustomer(customerId) {
  const res = await fetch(`${API_BASE}/customers/${customerId}`);
  if (!res.ok) throw new Error('Failed to load customer details.');
  return res.json();
}

async function listOrNull(url, what) {
  try {
    const res = await fetch(url);
    return res.ok ? await res.json() : null;
  } catch (err) {
    console.error(`Error loading ${what}:`, err);
    return null;
  }
}

/** GET /customers/{id}/contacts (the active ones, by name); null when it couldn't be read. */
export const loadContacts = (customerId) => listOrNull(`${API_BASE}/customers/${customerId}/contacts`, 'contacts');

/** GET /customers/{id}/addresses (the active ship-tos, default first); null when it couldn't be read. */
export const loadAddresses = (customerId) => listOrNull(`${API_BASE}/customers/${customerId}/addresses`, 'addresses');

/** GET /customers/{id}/tax_certificates: {certificate_status, certificate, certificates, customer, ...}.
 *  Throws the API's sentence. The Tax Certificates card and the phone's status line read it. */
export const loadTaxCertificates = (customerId) => apiJson(`${API_BASE}/customers/${customerId}/tax_certificates`);

/** A customer's certificate status in the office's words ("Certificate on file", ...); the raw value
 *  when it is one the page doesn't know. */
export function taxCertificateLabel(status) {
  return CUSTOMER_STATUS[status]?.label || status || 'Unknown';
}

/** A customer's tax rate as the office reads it: exempt at 0, else a percent ("8.5" for 0.085). */
export function taxRateDisplay(rate) {
  const rateVal = rate || 0;
  return { exempt: rateVal === 0, percent: (rateVal * 100).toString() };
}

// --- Contacts -----------------------------------------------------------------------------------------

export const CONTACT_ROLES = Object.freeze(['General', 'Billing', 'Shipping', 'Purchasing', 'Quotes']);

/** The contact form: a new contact (General), else the contact's. */
export function contactForm(c) {
  return { name: c?.name || '', email: c?.email || '', phone: c?.phone || '', role: c?.role || 'General' };
}

/** The request body: the fields as typed (only blank email / phone become null). */
export function contactPayload(form) {
  return { name: form.name, email: form.email || null, phone: form.phone || null, role: form.role };
}

/** Add (POST /customers/{customerId}/contacts) or edit (PATCH /contacts/{contactId}) a contact. A missing
 *  name and a refusal are said through ui.alert. setBusy(true) only once the request goes, false before
 *  a refusal is said. Resolves {done}. */
export async function saveContact({ customerId, contactId = null, form, ui, setBusy = () => {} }) {
  if (!String(form.name || '').trim()) {
    await ui.alert('Contact Name is required');
    return { done: false };
  }
  setBusy(true);
  try {
    const res = await fetch(contactId ? `${API_BASE}/contacts/${contactId}` : `${API_BASE}/customers/${customerId}/contacts`, {
      method: contactId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactPayload(form)),
    });
    if (!res.ok) throw new Error('Failed to save contact');
    setBusy(false);
    return { done: true };
  } catch (err) {
    setBusy(false);
    await ui.alert(err.message);
    return { done: false };
  }
}

// --- Ship-to addresses ------------------------------------------------------------------------------

/** The ship-to form: a new one (the default when it is the customer's first), else the address's. */
export function addressForm(a, { isFirst = false } = {}) {
  return {
    label: a?.label || '',
    address: a?.address || '',
    address_line2: a?.address_line2 || '',
    city: a?.city || '',
    state: a?.state || '',
    zip_code: a?.zip_code || '',
    contact_name: a?.contact_name || '',
    contact_phone: a?.contact_phone || '',
    delivery_notes: a?.delivery_notes || '',
    receiving_hours: a?.receiving_hours || '',
    is_default: a ? !!a.is_default : isFirst,
  };
}

/** The request body: every text trimmed, blank = null. */
export function addressPayload(form) {
  const t = (s) => String(s || '').trim() || null;
  return {
    label: String(form.label || '').trim(),
    address: t(form.address),
    address_line2: t(form.address_line2),
    city: t(form.city),
    state: t(form.state),
    zip_code: t(form.zip_code),
    contact_name: t(form.contact_name),
    contact_phone: t(form.contact_phone),
    delivery_notes: t(form.delivery_notes),
    receiving_hours: t(form.receiving_hours),
    is_default: form.is_default,
  };
}

/** Add (POST /customers/{customerId}/addresses) or edit (PATCH /addresses/{addressId}) a ship-to. A missing
 *  label and the API's refusal are said through ui.alert. setBusy as saveContact. Resolves {done}. */
export async function saveAddress({ customerId, addressId = null, form, ui, setBusy = () => {} }) {
  if (!String(form.label || '').trim()) {
    await ui.alert('A label (e.g. "Appleton HQ") is required');
    return { done: false };
  }
  setBusy(true);
  try {
    const res = await fetch(addressId ? `${API_BASE}/addresses/${addressId}` : `${API_BASE}/customers/${customerId}/addresses`, {
      method: addressId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(addressPayload(form)),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res));
    setBusy(false);
    return { done: true };
  } catch (err) {
    setBusy(false);
    await ui.alert(err.message);
    return { done: false };
  }
}
